import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// eslint-disable-next-line
import { useCreateCategoryMutation, useEditCategoryMutation, useUsersListQuery } from 'api';
import { QUERIES } from 'constants/index';
import { useCallbackPrompt } from 'hooks';
import {
  CreateCategoryFormValues,
  EditCategoryFormValues,
  SelectOption,
  User,
  UserRole,
  UserStatus,
} from 'types';
import { getFullName } from 'utils';

import { formValuesAdapter } from '../adapters';
import { AllOption } from '../constants';

interface UseCreateCategoryFormReturn {
  handleFormSubmit: () => void;
  handleAssigneeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  control: Control<CreateCategoryFormValues>;
  isLoading: boolean;
  handleCloseButton: () => void;
  isOpen: boolean;
  isDirty: boolean;
  isNotDisableButton: boolean;
  usersOptions: SelectOption[];
}

interface UseCreateCategoryFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  category: any;
  isEdit?: boolean;
  onClose?: () => void;
}

export function useCreateCategoryForm({
  category,
  isEdit,
  onClose,
}: UseCreateCategoryFormProps): UseCreateCategoryFormReturn {
  const categoryId = category?.id as string;
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isDirty, isSubmitted, errors, dirtyFields },
  } = useForm<CreateCategoryFormValues>({
    defaultValues: category,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const navigate = useNavigate();

  const { showPrompt } = useCallbackPrompt(
    (isDirty && !isSubmitted) || Object.keys(errors).length > 0,
  );

  const { createCategoryMutation, isLoading: isCreateCategoryLoading } = useCreateCategoryMutation({
    successText: t('toasts.category.success.create'),
    setIsSuccess,
    invalidateQueries: [QUERIES.LAST_UPDATED_DOCS, QUERIES.DOCUMENTS_LIST],
  });

  const { editCategoryMutation, isLoading: isEditCategoryLoading } = useEditCategoryMutation({
    successText: t('toasts.category.success.edit'),
    setIsSuccess,
    invalidateQueries: [QUERIES.LAST_UPDATED_DOCS, QUERIES.DOCUMENTS_LIST],
  });

  const handleAssigneeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value;

      if (!!value && value.length > 1) {
        if (value?.at(-1) === AllOption.value) {
          setValue('assigned_to', [AllOption.value]);
        } else if (value.includes(AllOption.value)) {
          setValue(
            'assigned_to',
            (value as unknown as string[]).filter((value) => value !== AllOption.value),
          );
        }
      }
    },
    [setValue],
  );

  const handleFormSubmit = handleSubmit(async (data) => {
    const { title, description, icon, assigned_to } = data;
    const categoryVisbility = assigned_to?.includes(AllOption.value) ? 'public' : 'private';

    if (isEdit) {
      const dirtyFieldsX = Object.keys(dirtyFields) as (keyof CreateCategoryFormValues)[];
      const updatedCategory: Partial<EditCategoryFormValues> = {};

      // We need to send the dirtyFields only with the update category calls
      dirtyFieldsX.forEach((dirtyField) => {
        // We need to pass title & description together, both should update together
        // Otherwise it may cause issue to translations
        if (dirtyField === 'title' || dirtyField === 'description') {
          updatedCategory['title'] = data['title'];
          updatedCategory['description'] = data['description'];
        } else {
          updatedCategory[dirtyField] = data[dirtyField];
        }
      });

      // Always we have to pass the visibility with update calls
      updatedCategory['visibility'] = categoryVisbility;
      updatedCategory['assigned_to'] = data?.[
        'assigned_to' as keyof CreateCategoryFormValues
      ] as string;

      // We should not pass assigned_to with non user id values
      if (assigned_to?.includes(AllOption.value)) {
        delete updatedCategory['assigned_to'];
      }

      const adaptedData = {
        category: updatedCategory,
        categoryId: categoryId,
      };

      await editCategoryMutation(adaptedData);
      return;
    }

    const adaptedData = {
      category: formValuesAdapter({
        title,
        description,
        icon,
        assigned_to,
        visibility: categoryVisbility,
      }),
      categoryId: categoryId,
    };

    if (assigned_to?.includes(AllOption.value)) {
      delete adaptedData.category['assigned_to'];
    }

    await createCategoryMutation(adaptedData);
  });

  function handleCloseButton() {
    if (onClose) {
      onClose();
      return;
    }
    navigate(-1);
  }

  useEffect(() => {
    if (isSubmitted && Object.keys(errors).length === 0 && isSuccess) {
      if (onClose) {
        onClose();
        return;
      }
      navigate(-1);
    }
  }, [isSubmitted, navigate, errors, isSuccess, onClose]);

  const { isLoading: isLoadingUsersList, usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });

  const usersOptions = usersList.map((user) => ({
    value: user.id,
    label:
      user.firstName && user.firstName
        ? getFullName({ firstName: user.firstName, lastName: user.lastName })
        : user.email,
    isManager: user.role === UserRole.Manager,
  }));

  useEffect(() => {
    if (category) {
      setValue('title', category.title);
      setValue('icon', category.icon);
      setValue(
        'assigned_to',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        category.assigned_to
          ? category.assigned_to.map((user: User) => user.id)
          : category.visibility === 'public'
          ? ['all']
          : [],
      );
    }
  }, [category, setValue]);

  return {
    control,
    handleFormSubmit,
    handleAssigneeChange,
    isLoading:
      isSubmitting || isEditCategoryLoading || isCreateCategoryLoading || isLoadingUsersList,
    handleCloseButton,
    isOpen: showPrompt,
    isDirty,
    isNotDisableButton: isDirty,
    usersOptions,
  };
}
